import { Filter } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  type PressureStatus,
  getStatusColor,
  type ViewMode,
} from '@/data/regulatoryData';

interface StatusFilterProps {
  activeFilters: Set<PressureStatus>;
  onFilterChange: (filters: Set<PressureStatus>) => void;
  viewMode: ViewMode;
}

type StatusConfig = {
  key: PressureStatus;
  pressureLabel: string;
  opportunityLabel: string;
  range: string;
};

const statusConfigs: StatusConfig[] = [
  { key: 'stable', pressureLabel: 'Stable', opportunityLabel: 'Low', range: '0-25' },
  { key: 'watch', pressureLabel: 'Watch', opportunityLabel: 'Moderate', range: '26-50' },
  { key: 'tightening', pressureLabel: 'Tightening', opportunityLabel: 'Good', range: '51-75' },
  { key: 'high', pressureLabel: 'High Pressure', opportunityLabel: 'Excellent', range: '76-100' },
];

const allStatuses: PressureStatus[] = ['stable', 'watch', 'tightening', 'high'];

/**
 * Status filter dropdown allowing multi-select filtering of jurisdictions by status level.
 * Labels change based on view mode (Pressure vs Opportunity).
 */
export function StatusFilter({ activeFilters, onFilterChange, viewMode }: StatusFilterProps) {
  const handleToggle = (status: PressureStatus) => {
    const newFilters = new Set(activeFilters);
    if (newFilters.has(status)) {
      newFilters.delete(status);
    } else {
      newFilters.add(status);
    }
    onFilterChange(newFilters);
  };

  const handleSelectAll = () => {
    onFilterChange(new Set(allStatuses));
  };

  const handleClearAll = () => {
    onFilterChange(new Set());
  };

  const activeCount = activeFilters.size;
  const isAllSelected = activeCount === allStatuses.length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`glass rounded-lg shadow-glass h-[38px] px-3 flex items-center gap-2
                     hover:bg-white/10 transition-colors text-sm
                     ${activeCount < allStatuses.length ? 'ring-1 ring-brand/50' : ''}`}
          title="Filter by status (F)"
        >
          <Filter className="h-4 w-4 text-text-tertiary" />
          <span className="text-text-primary">
            {isAllSelected ? 'Filter' : `Filter (${activeCount})`}
          </span>
          <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-text-tertiary text-[10px]">
            F
          </kbd>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="glass-strong w-56"
        align="end"
        sideOffset={8}
      >
        {statusConfigs.map((config) => {
          const color = getStatusColor(config.key);
          const label = viewMode === 'pressure' ? config.pressureLabel : config.opportunityLabel;

          return (
            <DropdownMenuCheckboxItem
              key={config.key}
              checked={activeFilters.has(config.key)}
              onCheckedChange={() => handleToggle(config.key)}
              className="cursor-pointer"
            >
              <div className="flex items-center gap-2 flex-1">
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: color }}
                />
                <span className="text-text-primary">{label}</span>
                <span className="text-text-tertiary text-xs ml-auto">({config.range})</span>
              </div>
            </DropdownMenuCheckboxItem>
          );
        })}

        <DropdownMenuSeparator />

        <div className="flex items-center justify-between px-2 py-1.5">
          <button
            onClick={handleClearAll}
            className="text-xs text-text-tertiary hover:text-text-primary transition-colors"
          >
            Clear all
          </button>
          <button
            onClick={handleSelectAll}
            className="text-xs text-text-tertiary hover:text-text-primary transition-colors"
          >
            Select all
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default StatusFilter;
